/**
 * Where a team's Rules live: a Git repository of their own (§5.27), never a
 * folder inside the code they govern. A workspace names one in its shared
 * settings (`config.rulesRepository`), and has it among its repositories too;
 * an organization names none of its own since 2026-10-23.
 *
 * A rules repository holds one JSON file per RFC, named after its number and
 * slug — `0012-api-errors.json` — and the check scripts its statements name
 * under `checks/` (`CHECK_FILE`); nothing else of it is read. Where the file
 * sits says which code repositories it reaches, named by their remote's last
 * says which projects of the workspace it reaches, each by the folder name the
 * workspace gives it (`folders`, Settings › Rules) — else its sub-folder's last
 * segment in a repository of several projects (`apps/web` is `web`), else its
 * remote's last segment (`github.com/acme/web` is `web`): at the top level,
 * every repository of the session; in `web/`, that project alone; in
 * `web+api/`, those two — how one rule ties two projects together, in one
 * repository or in two. In a folder, a glob reads from the project's own
 * folder, and in a folder of several it may name one of them in front
 * (`web:src/client/**`); a glob without one reaches each project of the folder.
 *
 * Every machine reads the repository with its own Git access; the account
 * reads it through the organization's GitHub App. Nothing here touches Git —
 * this is the shape both sides agree on.
 */
import { normalizeRemote, type RemoteIdentity } from '../workspace/remote';
import { isPlainObject } from '../workspace/shape';

export interface RulesRepoRef {
  /** As the person wrote it — `github.com/acme/engineering-rules`, `git@github.com:acme/engineering-rules.git`. */
  remote: string;
  /** Null for the repository's default branch. */
  branch: string | null;
  /**
   * The folder name each project of the workspace takes here, where it is not
   * its default (`projectFolderDefault`) — so a name an organization's
   * convention makes long (`acme-platform-web-frontend`) is `web`, and a
   * project renamed or moved keeps its rules. Present only when it holds one.
   */
  folders?: RulesProjectFolder[];
}

/**
 * One project's folder name: the project by its repository's remote identity
 * (`github.com/acme/platform`) and its folder in that repository (`apps/web`,
 * absent for the repository itself) — the same on every machine and in the
 * account, where a project's id and name are not.
 */
export interface RulesProjectFolder {
  remote: string;
  subPath?: string;
  folder: string;
}

/** The most projects a rules repository names a folder for. */
export const MAX_RULE_FOLDERS = 200;

/** A project as a rules repository names it — `web`, read in lower case. */
const REPO_NAME_SOURCE = '[A-Za-z0-9_][A-Za-z0-9._-]{0,99}';
export const REPO_NAME = new RegExp(`^${REPO_NAME_SOURCE}$`);

/**
 * An RFC file of a rules repository: `NNNN-slug.json` at its top level, or one
 * folder down in a folder naming its repositories — `web/`, `web+api/`. The
 * `checks/` folder is the scripts', never a repository's.
 */
export const RULE_FILE = new RegExp(
  `^(?:(?!checks/)${REPO_NAME_SOURCE}(?:\\+${REPO_NAME_SOURCE})*/)?\\d{1,5}-[a-z0-9]+(?:-[a-z0-9]+)*\\.json$`
);

/** The most repositories one RFC's folder may name. */
export const MAX_RULE_REPOS = 12;

/**
 * A statement's check script: `checks/<name>.py` in the same rules repository,
 * run on the files of a change in the session's machine instead of an agent
 * reading for the statement (`document.ts`, the desktop's `rules/checks.ts`).
 */
export const CHECK_FILE = /^checks\/[a-z0-9]+(?:-[a-z0-9]+)*\.py$/;

/** A check script read at most this long — a check is a small program, not a vendored tree. */
export const MAX_CHECK_CHARS = 64 * 1024;

/**
 * The file an RFC is written to: in the folder of the repositories it reaches,
 * named in alphabetical order, or at the top level when it reaches every one.
 */
export function ruleFileName(rfc: number, slug: string, repos: readonly string[] = []): string {
  const folder = ruleFolder(repos);
  return `${folder ? `${folder}/` : ''}${String(rfc).padStart(4, '0')}-${slug}.json`;
}

/** The folder of these repositories, `api+web` — empty for every repository. */
export function ruleFolder(repos: readonly string[]): string {
  return [...new Set(repos.map((one) => one.toLowerCase()))].sort().join('+');
}

/** The repositories a rule file's folder names, `web+api/0004-x.json` → `['api', 'web']`; none at the top level. */
export function ruleFileRepos(path: string): string[] {
  const slash = path.lastIndexOf('/');
  return slash < 0 ? [] : [...new Set(path.slice(0, slash).toLowerCase().split('+'))].sort();
}

/** The name a rules repository gives a code repository by default: its remote identity's last segment, or null with no remote. */
export function repoNameOf(identity: string | null): string | null {
  if (!identity) return null;
  const name = identity.toLowerCase().split('/').pop() ?? '';
  return REPO_NAME.test(name) ? name : null;
}

/** A project's folder name when the workspace names none: its sub-folder's last segment, else its repository's (`repoNameOf`). */
export function projectFolderDefault(identity: string | null, subPath?: string | null): string | null {
  const segment = subPath?.replace(/\/+$/, '').split('/').pop()?.toLowerCase();
  if (segment) return REPO_NAME.test(segment) && segment !== 'checks' ? segment : null;
  return repoNameOf(identity);
}

/** The folder name a project takes in this rules repository: the one the workspace gave it, else its default. */
export function projectFolderOf(
  ref: Pick<RulesRepoRef, 'folders'> | null | undefined,
  identity: string | null,
  subPath?: string | null
): string | null {
  const given = identity ? ref?.folders?.find((one) => sameProject(one, identity, subPath)) : undefined;
  return given?.folder ?? projectFolderDefault(identity, subPath);
}

function sameProject(entry: RulesProjectFolder, identity: string, subPath?: string | null): boolean {
  return entry.remote.toLowerCase() === identity.toLowerCase() && (entry.subPath ?? '') === (subPath ?? '');
}

/**
 * The rules repository with one project's folder name set — kept even when it
 * is the default, so the name stays if the remote or the folder is renamed;
 * removed when empty, the project then taking its default. The name is kept
 * as typed; the document reads it in lower case.
 */
export function withProjectFolder(ref: RulesRepoRef, remote: string, subPath: string | null | undefined, folder: string): RulesRepoRef {
  const rest = (ref.folders ?? []).filter((one) => !sameProject(one, remote, subPath));
  const name = folder.trim();
  const kept = name ? [...rest, { remote, ...(subPath ? { subPath } : {}), folder: name }] : rest;
  const { folders: _old, ...base } = ref;
  return kept.length ? { ...base, folders: kept } : base;
}

/** Why a folder name is refused, or null. */
export function ruleFolderProblem(folder: string): string | null {
  if (!REPO_NAME.test(folder)) return 'Use letters, digits, dots, underscores and hyphens, starting with a letter, a digit or an underscore.';
  return folder.toLowerCase() === 'checks' ? 'checks holds the check scripts — choose another name.' : null;
}

/** A branch name git would take: no space, no `..`, no leading `-` or `/`, no lock suffix. */
// eslint-disable-next-line no-control-regex -- a control character in a branch name is not one.
const BRANCH = /^(?!-)(?!\/)(?!.*\.\.)(?!.*\/\/)(?!.*@\{)(?!.*\.lock$)(?!.*\/$)[^\s~^:?*[\\\x00-\x1f\x7f]{1,200}$/;

export const REMOTE_EXAMPLE = 'github.com/acme/engineering-rules or git@github.com:acme/engineering-rules.git';

/**
 * A rules repository's remote as Git can reach it. Besides every spelling
 * `normalizeRemote` reads, the short `github.com/acme/rules` a person types
 * is read as its https URL — the form the field's example offers.
 */
export function rulesRemoteIdentity(remote: string): RemoteIdentity | null {
  const trimmed = remote.trim();
  return normalizeRemote(trimmed) ?? (/^[^:@\s]+\.[^:@\s/]+\/[^:@\s]+$/.test(trimmed) ? normalizeRemote(`https://${trimmed}`) : null);
}

/** Why a remote cannot be a rules repository, or null — the sentence the field says under itself. */
export function rulesRemoteProblem(remote: string): string | null {
  return rulesRemoteIdentity(remote) ? null : `Enter a Git remote such as ${REMOTE_EXAMPLE}.`;
}

/** Why a branch name is refused, or null. Empty is the default branch. */
export function rulesBranchProblem(branch: string): string | null {
  return !branch || BRANCH.test(branch) ? null : 'Enter a branch name as Git spells it, such as main or release/rules.';
}

/** As a stored document carries it: what does not read is no repository, never a failure. */
export function rulesRepoRefOf(raw: unknown): RulesRepoRef | null {
  if (!isPlainObject(raw) || typeof raw.remote !== 'string') return null;
  const remote = raw.remote.trim();
  if (!remote || rulesRemoteProblem(remote)) return null;
  const branch = typeof raw.branch === 'string' && raw.branch.trim() && !rulesBranchProblem(raw.branch.trim()) ? raw.branch.trim() : null;
  const folders = foldersOf(raw.folders, () => undefined);
  return { remote, branch, ...(folders?.length ? { folders } : {}) };
}

/**
 * The projects' folder names as written, each normalized — the remote as its
 * identity, the sub-folder without its slashes at the ends, the name in lower
 * case, one entry per project (the last kept); null with a sentence per fault.
 */
function foldersOf(value: unknown, fault: (at: string, msg: string) => void): RulesProjectFolder[] | null {
  if (value === undefined || value === null) return [];
  if (!Array.isArray(value) || value.length > MAX_RULE_FOLDERS) {
    fault('', `Expected a list of at most ${MAX_RULE_FOLDERS} { "remote", "subPath", "folder" }.`);
    return null;
  }
  const out = new Map<string, RulesProjectFolder>();
  let refused = false;
  value.forEach((entry, index) => {
    const at = `[${index}]`;
    if (!isPlainObject(entry)) {
      fault(at, 'Expected { "remote": "…", "subPath": "…", "folder": "…" }.');
      refused = true;
      return;
    }
    for (const key of Object.keys(entry)) {
      if (key !== 'remote' && key !== 'subPath' && key !== 'folder') {
        fault(`${at}.${key}`, 'Not a field this version of Airlock knows.');
        refused = true;
      }
    }
    const identity = typeof entry.remote === 'string' ? (rulesRemoteIdentity(entry.remote)?.remote ?? null) : null;
    if (!identity) {
      fault(`${at}.remote`, 'Expected the remote of one of the workspace’s repositories.');
      refused = true;
    }
    const subPath = typeof entry.subPath === 'string' ? entry.subPath.trim().replace(/^\/+|\/+$/g, '') : '';
    if (entry.subPath !== undefined && typeof entry.subPath !== 'string') {
      fault(`${at}.subPath`, 'Expected a folder of the repository, such as apps/web.');
      refused = true;
    }
    const folder = typeof entry.folder === 'string' ? entry.folder.trim() : '';
    const problem = ruleFolderProblem(folder);
    if (problem) {
      fault(`${at}.folder`, problem);
      refused = true;
    }
    if (identity && !problem) out.set(`${identity}\n${subPath}`, { remote: identity, ...(subPath ? { subPath } : {}), folder: folder.toLowerCase() });
  });
  return refused ? null : [...out.values()];
}

/** As a person wrote it — refused with a sentence per fault, `at` naming the field. */
export function parseRulesRepoRef(input: unknown, at: string, problems: string[]): RulesRepoRef | null {
  if (input === undefined || input === null) return null;
  if (!isPlainObject(input)) {
    problems.push(`${at}: Expected { "remote": "…", "branch": "…" }.`);
    return null;
  }
  for (const key of Object.keys(input)) {
    if (key !== 'remote' && key !== 'branch' && key !== 'folders') problems.push(`${at}.${key}: Not a field this version of Airlock knows.`);
  }
  const remote = typeof input.remote === 'string' ? input.remote.trim() : '';
  const branch = typeof input.branch === 'string' ? input.branch.trim() : input.branch === null || input.branch === undefined ? '' : null;
  const before = problems.length;
  const remoteProblem = rulesRemoteProblem(remote);
  if (remoteProblem) problems.push(`${at}.remote: ${remoteProblem}`);
  if (branch === null) problems.push(`${at}.branch: Expected a branch name, or nothing for the default branch.`);
  else {
    const branchProblem = rulesBranchProblem(branch);
    if (branchProblem) problems.push(`${at}.branch: ${branchProblem}`);
  }
  const folders = foldersOf(input.folders, (where, msg) => problems.push(`${at}.folders${where}: ${msg}`));
  return problems.length > before ? null : { remote, branch: branch || null, ...(folders?.length ? { folders } : {}) };
}

/** The identity two spellings of one rules repository share, `github.com/acme/engineering-rules`. */
export function rulesRepoKey(ref: Pick<RulesRepoRef, 'remote'>): string {
  return rulesRemoteIdentity(ref.remote)?.remote ?? ref.remote;
}

/**
 * A glob read apart: the project it names, if any, and the path glob.
 * `web:src/**` names `web`; `src/**` names none and reaches every project
 * its RFC's folder names.
 */
export function splitRepoGlob(glob: string): { repo: string | null; glob: string } {
  const m = new RegExp(`^(${REPO_NAME_SOURCE}):(.+)$`).exec(glob);
  return m ? { repo: m[1].toLowerCase(), glob: m[2] } : { repo: null, glob };
}
